import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../../actions';
import { showConfirmationModal } from '../../../actions/App';
import { fetchTaxonomies } from '../../../actions/Metadata';

import { APIdeleteTaxonomy } from '../../../api/Metadata';

import {
  customCategoryTarget,
  customCategorySource,
} from '../../../helpers/DNDCustomCategory';
import ItemTypes from '../../../helpers/DNDItemTypes';
import { getCurrentURLParams } from '../../../helpers/URLParams';
import { userHasFeature } from '../../../helpers/Features';
import { getBackButtonMeta } from '../../../helpers/App';

import UpgradePlanButton from '../../UpgradePlanButton';
import EmptyTableCell from '../../EmptyTableCell';

const AdminTaxonomiesListItem = ({
  intl,
  taxonomy,
  handleDeleteTaxonomy,
  deleteTaxonomyFromList,
  isDragging,
  connectDragSource,
  connectDropTarget,
  ai,
}) => {
  const location = useLocation();

  const linkTo = {
    pathname: `/admin/settings/categories/${taxonomy.id}/edit`,
    state: {
      goBackToListButton: {
        ...getBackButtonMeta('taxonomies'),
        search: getCurrentURLParams(location),
      },
    },
  };

  const shouldBeDisabled = !userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging);

  return connectDragSource(connectDropTarget((
    <tr className={classnames({ dragging: isDragging })}>
      <th scope="row" className="wrapper-drag-and-drop-icon">
        {ai ? (
          <>
            <i
              id={`AiTaxonomyIcon-${taxonomy.id}`}
              className="fa-solid fa-sparkles"
            />
            <UncontrolledTooltip
              target={`AiTaxonomyIcon-${taxonomy.id}`}
            >
              {intl.formatMessage({ defaultMessage: 'Auto tagging category' })}
            </UncontrolledTooltip>
          </>
        ) : <i className="fa-solid fa-grip-vertical" />}
      </th>
      <th scope="row" className="taxonomy-name">
        <span className="taxonomy-name-content">
          <Link to={linkTo} className={classnames({ disabled: shouldBeDisabled })}>
            {taxonomy.name}
          </Link>
          {shouldBeDisabled && (
            <UpgradePlanButton feature="custom-categories" />
          )}
        </span>
      </th>
      <td className={classnames('taxonomy-plural', { disabled: shouldBeDisabled })}>
        {taxonomy.plural}
      </td>
      <td className={classnames('taxonomy-files', { disabled: shouldBeDisabled })}>
        <Link to={`/admin/settings/categories/${taxonomy.id}/edit`}>
          {taxonomy.terms.length}
        </Link>
      </td>
      <td className={classnames('taxonomy-show-on-shares', { disabled: shouldBeDisabled })}>
        {taxonomy.show_on_shares ? (
          <i className="far fa-check" />
        ) : (
          <EmptyTableCell />
        )}
      </td>
      <td className={classnames('taxonomy-required', { disabled: shouldBeDisabled })}>
        {taxonomy.required ? (
          <i className="far fa-check" />
        ) : (
          <EmptyTableCell />
        )}
      </td>
      <td className={classnames('action-more', { disabled: shouldBeDisabled })}>
        <UncontrolledDropdown>
          <DropdownToggle nav className="options">
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right>
            <Link to={linkTo} className="dropdown-item-link">
              <DropdownItem tag="span">
                <i className="fas fa-pencil" />
                {intl.formatMessage({ defaultMessage: 'Edit' })}
              </DropdownItem>
            </Link>
            <DropdownItem divider />
            <DropdownItem
              onClick={(e) => {
                e.preventDefault();
                handleDeleteTaxonomy(taxonomy, deleteTaxonomyFromList);
              }}
            >
              <i className="fas fa-trash" />
              {intl.formatMessage({ defaultMessage: 'Delete' })}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </td>
    </tr>
  )));
};

const mapDispatchToProps = (dispatch, props) => ({
  openTaxonomyDeleteModal: (params) => {
    dispatch(showModal('TaxonomyDeleteModal', params));
  },
  handleDeleteTaxonomy: (taxonomy, callback) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: `Are you sure you want to delete this Tag Category?
          Doing this will delete ALL the tags associated with this Tag
          Category and they cannot be recovered. Files will NOT be deleted.`,
      }),
      onConfirm: () => (
        APIdeleteTaxonomy(taxonomy.id).then(() => {
          dispatch(fetchTaxonomies());
          callback(taxonomy);
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
});

AdminTaxonomiesListItem.propTypes = {
  intl: PropTypes.object.isRequired,
  taxonomy: PropTypes.object.isRequired,
  handleDeleteTaxonomy: PropTypes.func.isRequired,
  deleteTaxonomyFromList: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  ai: PropTypes.bool.isRequired,
};

AdminTaxonomiesListItem.defaultProps = {
  ai: false,
};

const enhance = compose(
  DragSource(ItemTypes.CUSTOM_CATEGORY, customCategorySource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    isDragging: monitor.isDragging(),
  })),
  DropTarget(ItemTypes.CUSTOM_CATEGORY, customCategoryTarget, DNDconnect => ({
    connectDropTarget: DNDconnect.dropTarget(),
  })),
  connect(null, mapDispatchToProps),
);

export default injectIntl(enhance(AdminTaxonomiesListItem));
